import React, { useState, useRef, useEffect } from 'react';
// FIX: Corrected Firebase User type usage.
import firebase from 'firebase/compat/app';
import { auth } from '../services/firebase';
import { ExportIcon, PdfIcon, LogoutIcon, AddIcon, ImportIcon, MenuIcon, ProfileIcon, MoreVerticalIcon, ShieldIcon, FileTextIcon, GavelIcon, CookieIcon, SunIcon, MoonIcon, SystemIcon, MicrophoneIcon } from './icons';

type Theme = 'light' | 'dark' | 'system';

interface HeaderProps {
  title: string;
  user: firebase.User | null;
  onAddClick: () => void;
  onImportClick: () => void;
  isImporting: boolean;
  onExportClick: (format: 'Excel' | 'PDF') => void;
  toggleSidebar: () => void;
  onOpenProfileModal: () => void;
  onOpenLegalModal: (type: 'privacy' | 'terms' | 'legal' | 'cookies') => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  onVoiceCaptureClick: () => void;
}

const ThemeSwitcher: React.FC<{ theme: Theme; setTheme: (theme: Theme) => void }> = ({ theme, setTheme }) => {
    // FIX: Changed icon type to allow className prop in React.cloneElement
    const themes: { name: Theme, icon: React.ReactElement<any>, label: string }[] = [
        { name: 'light', icon: <SunIcon className="w-5 h-5 mr-3"/>, label: "Tema Claro" },
        { name: 'dark', icon: <MoonIcon className="w-5 h-5 mr-3"/>, label: "Tema Oscuro" },
        { name: 'system', icon: <SystemIcon className="w-5 h-5 mr-3"/>, label: "Tema del Sistema" },
    ];

    return (
        <div>
            <div className="px-4 pt-2 pb-1 text-xs text-light-text-secondary dark:text-text-secondary">Cambiar Tema</div>
            <div className="flex justify-around items-center p-2">
                {themes.map(({ name, icon, label }) => (
                    <button
                        key={name}
                        onClick={() => setTheme(name)}
                        className={`p-2 rounded-lg transition-colors ${
                            theme === name 
                                ? 'bg-primary text-white' 
                                : 'text-light-text-secondary dark:text-text-secondary hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                        title={label}
                    >
                        {React.cloneElement(icon, { className: 'w-6 h-6' })}
                    </button>
                ))}
            </div>
        </div>
    );
};

const Header: React.FC<HeaderProps> = ({ title, user, onAddClick, onImportClick, isImporting, onExportClick, toggleSidebar, onOpenProfileModal, onOpenLegalModal, theme, setTheme, onVoiceCaptureClick }) => {
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  
  const handleLogout = async () => {
    if (!auth) {
      console.error('Firebase Auth no está inicializado.');
      return;
    }
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error al cerrar sesión: ', error);
    }
  };
  
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
            setIsUserDropdownOpen(false);
        }
        if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
            setIsExportDropdownOpen(false);
        }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const userDisplayName = user?.displayName || user?.email;

  return (
    <header className="bg-light-surface dark:bg-surface border-b border-light-border dark:border-border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-2 flex-shrink-0">
      <div className="flex items-center">
        <button id="mobile-menu-button" onClick={toggleSidebar} className="lg:hidden p-2 mr-3 text-light-text-secondary dark:text-text-secondary hover:text-light-text-primary dark:hover:text-text-primary">
          <MenuIcon className="w-6 h-6" />
        </button>
        <h2 className="text-xl sm:text-2xl font-bold text-light-text-primary dark:text-text-primary">{title}</h2>
      </div>
      <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto space-x-2 sm:space-x-4">
        <div id="header-actions" className="flex items-center flex-wrap gap-2">
            <button 
              onClick={onVoiceCaptureClick}
              className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 text-sm font-medium shadow-sm">
              <MicrophoneIcon className="w-5 h-5 sm:mr-2" />
              <span className="hidden sm:inline">Voz</span>
            </button>
            <button 
              onClick={onAddClick}
              className="flex items-center px-3 py-2 bg-secondary text-white rounded-lg hover:bg-green-600 transition-colors duration-200 text-sm font-medium shadow-sm">
              <AddIcon className="w-5 h-5 sm:mr-2" />
              <span className="hidden sm:inline">Añadir</span>
            </button>
            <button 
              onClick={onImportClick}
              disabled={isImporting}
              className="flex items-center px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 text-sm font-medium shadow-sm disabled:bg-gray-500 disabled:cursor-wait">
              <ImportIcon className="w-5 h-5 sm:mr-2" />
              <span className="hidden sm:inline">{isImporting ? 'Procesando...' : 'Importar'}</span>
            </button>
            <div className="relative" ref={exportDropdownRef}>
                <button 
                    onClick={() => setIsExportDropdownOpen(prev => !prev)}
                    className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 text-sm font-medium shadow-sm">
                    <ExportIcon className="w-5 h-5 sm:mr-2" />
                    <span className="hidden sm:inline">Exportar</span>
                </button>
                {isExportDropdownOpen && (
                    <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-48 bg-light-surface dark:bg-surface rounded-md shadow-lg py-1 border border-light-border dark:border-border z-50">
                        <button 
                            onClick={() => { onExportClick('Excel'); setIsExportDropdownOpen(false); }} 
                            className="w-full text-left flex items-center px-4 py-2 text-sm text-light-text-secondary dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-light-text-primary dark:hover:text-text-primary">
                            <ExportIcon className="w-5 h-5 mr-3 text-green-500"/> Exportar a Excel
                        </button>
                        <button 
                            onClick={() => { onExportClick('PDF'); setIsExportDropdownOpen(false); }} 
                            className="w-full text-left flex items-center px-4 py-2 text-sm text-light-text-secondary dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-light-text-primary dark:hover:text-text-primary">
                            <PdfIcon className="w-5 h-5 mr-3 text-red-500"/> Exportar a PDF
                        </button>
                    </div>
                )}
            </div>
        </div>
        
        <div id="user-profile-menu" className="relative" ref={userDropdownRef}>
            <div className="flex items-center space-x-2 sm:space-x-3 sm:pl-4 sm:border-l border-light-border dark:border-border cursor-pointer" onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}>
                <img src={`https://api.dicebear.com/8.x/initials/svg?seed=${userDisplayName}`} alt="User" className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 border-2 border-primary flex-shrink-0" />
                <span className="font-medium text-light-text-primary dark:text-text-primary hidden md:inline truncate max-w-[150px]">{userDisplayName}</span>
            </div>
            {isUserDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-light-surface dark:bg-surface rounded-md shadow-lg py-1 border border-light-border dark:border-border z-50">
                    <button onClick={() => { onOpenProfileModal(); setIsUserDropdownOpen(false); }} className="w-full text-left flex items-center px-4 py-2 text-sm text-light-text-secondary dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-light-text-primary dark:hover:text-text-primary">
                        <ProfileIcon className="w-5 h-5 mr-3"/> Mi Perfil
                    </button>
                    <div className="my-1 border-t border-light-border dark:border-border"></div>
                    <ThemeSwitcher theme={theme} setTheme={setTheme} />
                    <div className="my-1 border-t border-light-border dark:border-border"></div>
                    <button onClick={() => { onOpenLegalModal('legal'); setIsUserDropdownOpen(false); }} className="w-full text-left flex items-center px-4 py-2 text-sm text-light-text-secondary dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-light-text-primary dark:hover:text-text-primary">
                        <GavelIcon className="w-5 h-5 mr-3"/> Aviso Legal
                    </button>
                     <button onClick={() => { onOpenLegalModal('privacy'); setIsUserDropdownOpen(false); }} className="w-full text-left flex items-center px-4 py-2 text-sm text-light-text-secondary dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-light-text-primary dark:hover:text-text-primary">
                        <ShieldIcon className="w-5 h-5 mr-3"/> Política de Privacidad
                    </button>
                    <button onClick={() => { onOpenLegalModal('terms'); setIsUserDropdownOpen(false); }} className="w-full text-left flex items-center px-4 py-2 text-sm text-light-text-secondary dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-light-text-primary dark:hover:text-text-primary">
                        <FileTextIcon className="w-5 h-5 mr-3"/> Términos de Uso
                    </button>
                     <button onClick={() => { onOpenLegalModal('cookies'); setIsUserDropdownOpen(false); }} className="w-full text-left flex items-center px-4 py-2 text-sm text-light-text-secondary dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-light-text-primary dark:hover:text-text-primary">
                        <CookieIcon className="w-5 h-5 mr-3"/> Política de Cookies
                    </button>
                    <div className="my-1 border-t border-light-border dark:border-border"></div>
                    <button onClick={handleLogout} className="w-full text-left flex items-center px-4 py-2 text-sm text-light-text-secondary dark:text-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-light-text-primary dark:hover:text-text-primary">
                        <LogoutIcon className="w-5 h-5 mr-3"/> Cerrar sesión
                    </button>
                </div>
            )}
        </div>
      </div>
    </header>
  );
};

export default Header;